import { useEffect, useMemo, useState } from "react";
import { Drawer, Modal, Select, message } from "antd";
import { CareShell, Panel } from "../../components/care/CareLayout";
import Guard from "../../components/access-control/rbac/Guard";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { careService } from "../../services/careService";
import olreadiaService from "../../services/olreadiaService";

const clean = (value) => String(value || "").trim();
const normalizeRoleToken = (role) =>
  String(role || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
const today = () => new Date().toISOString().slice(0, 10);
const pad = (part) => String(part).padStart(2, "0");
const localDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const daysFromNow = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
};
const parseDateKey = (value) => {
  const [year, month, day] = String(value || today()).split("-").map((part) => Number(part));
  return new Date(year || new Date().getFullYear(), (month || 1) - 1, day || 1);
};
const monthLabel = (date) =>
  date.toLocaleDateString([], { month: "long", year: "numeric" });
const addMonths = (date, delta) => new Date(date.getFullYear(), date.getMonth() + delta, 1);
const sameDay = (left, right) => localDateKey(left) === localDateKey(right);
const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const calendarDaysFor = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};
const calendarRangeForMonth = (monthDate) => {
  const days = calendarDaysFor(monthDate);
  return {
    startDate: localDateKey(days[0]),
    endDate: localDateKey(days[days.length - 1]),
  };
};
const scheduleStartValue = (schedule = {}) => {
  const source = schedule || {};
  return source.startTimestamp || source.startAt || source.startDateTime || source.startTime || "";
};
const scheduleEndValue = (schedule = {}) => {
  const source = schedule || {};
  return source.endTimestamp || source.endAt || source.endDateTime || source.endTime || "";
};
const overlapsDay = (schedule = {}, date) => {
  const start = new Date(scheduleStartValue(schedule));
  const end = new Date(scheduleEndValue(schedule));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false;
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  return start <= dayEnd && end >= dayStart;
};
const timeRangeLabel = (schedule = {}) => {
  const start = new Date(scheduleStartValue(schedule));
  const end = new Date(scheduleEndValue(schedule));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return `${start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} - ${end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
};
const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";
const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};
const userFor = (profile = {}) => profile?.userProfile || profile?.user || {};
const profileFor = (profile = {}) => profile?.profile || profile?.profileData || {};
const profileIdFor = (profile = {}) => profile.id || profile.careProfileId || "";
const userIdFor = (profile = {}) => {
  const user = userFor(profile);
  return user.id || profile.userProfileId || profile.userId || "";
};
const nameFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.name) ||
    clean(base.fullName) ||
    [base.firstName, base.middleName, base.lastName].map(clean).filter(Boolean).join(" ") ||
    clean(profile.name) ||
    "Care staff";
};
const emailFor = (profile = {}) => {
  const user = userFor(profile);
  const base = profileFor(profile);
  return clean(user.email) || clean(base.email) || "";
};
const staffNameFor = (schedule = {}, profilesById = {}) => {
  const source = schedule || {};
  const profile = profilesById[source.careProfileId] ||
    profilesById[source.caregiverProfileId] ||
    profilesById[source.careGiverProfileId] ||
    profilesById[source.caregiverUserId] ||
    profilesById[source.careGiverUserId];
  return clean(source.caregiverName) || (profile ? nameFor(profile) : "") || clean(source.caregiverEmail) || "Care staff";
};
const staffEmailFor = (schedule = {}, profilesById = {}) => {
  const source = schedule || {};
  const profile = profilesById[source.careProfileId] ||
    profilesById[source.caregiverProfileId] ||
    profilesById[source.careGiverProfileId] ||
    profilesById[source.caregiverUserId] ||
    profilesById[source.careGiverUserId];
  return clean(source.caregiverEmail) || (profile ? emailFor(profile) : "");
};
const labelFor = (value) => String(value || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const statusIconFor = (status) => {
  const normalized = String(status || "").trim().toUpperCase();
  if (normalized === "COMPLETED") return "ti-circle-check";
  if (normalized === "CANCELLED" || normalized === "CANCELED") return "ti-ban";
  return "ti-clock";
};
const statusClassFor = (status) => String(status || "ACTIVE").trim().toLowerCase().replace(/_/g, "-");
const organizationLocationsOf = (organization) =>
  organization?.businessLocations || organization?.organizationLocations || organization?.locations || [];
const addressFromLocationInfo = (info = {}) => {
  const source = info?.locationInformation || info?.address || info?.location || info || {};
  return clean(source.address) ||
    clean(source.formattedAddress) ||
    [source.addressLine1, source.addressLine2, source.city, source.state || source.stateCode, source.zipCode || source.postalCode, source.country || source.countryCode]
      .map(clean)
      .filter(Boolean)
      .join(", ");
};
const scheduleTitleFor = (staff, scheduleType) => {
  if (!staff?.name && !staff?.email) return "";
  const typeLabel = scheduleType === "ON_CALL" ? "On Call" : "Normal Schedule";
  return `${staff.name || "Care staff"}${staff.email ? ` (${staff.email})` : ""} - ${typeLabel}`;
};
const formatTimestamp = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};
const scheduleDurationLabel = (schedule = {}) => {
  const start = new Date(scheduleStartValue(schedule));
  const end = new Date(scheduleEndValue(schedule));
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return "N/A";
  const totalMinutes = Math.round((end.getTime() - start.getTime()) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const hourLabel = hours ? `${hours} hr${hours === 1 ? "" : "s"}` : "";
  const minuteLabel = minutes ? `${minutes} min${minutes === 1 ? "" : "s"}` : "";
  return [hourLabel, minuteLabel].filter(Boolean).join(" ") || "0 mins";
};
const excelCell = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const filenameDate = () => new Date().toISOString().slice(0, 10);
const dateTimeInputValue = (value, fallbackDate = today(), fallbackTime = "09:00") => {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return `${fallbackDate}T${fallbackTime}`;
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}T${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
};
const zonedIsoFromDateTimeInput = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
};
const dateKeyFromTimestamp = (value) => {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return today();
  return localDateKey(parsed);
};
const scheduleIdFor = (schedule = {}) => {
  const source = schedule || {};
  return String(source.id || source.scheduleId || "");
};
const scheduleStatusValue = (schedule = {}) => {
  const source = schedule || {};
  return String(source.status || "").trim().toUpperCase();
};
const scheduleStatusOptions = ["ACTIVE", "CANCELLED", "COMPLETED"];
const backendScheduleStatuses = new Set(scheduleStatusOptions);
const scheduleIsActive = (schedule = {}) => scheduleStatusValue(schedule) === "ACTIVE";
const statusIsActive = (status) => String(status || "").trim().toUpperCase() === "ACTIVE";
const rangesOverlap = (startA, endA, startB, endB) => startA < endB && endA > startB;
const scheduleStaffIds = (schedule = {}) => {
  const source = schedule || {};
  return [
    source.careProfileId,
    source.caregiverProfileId,
    source.careGiverProfileId,
    source.profileId,
    source.careStaffId,
    source.staffId,
    source.caregiverUserId,
    source.careGiverUserId,
    source.userId,
    source.assignedUserId,
    source.caregiverId,
    source.careGiverId,
  ].map((value) => String(value || "")).filter(Boolean);
};
const sameCareStaffSchedule = (schedule = {}, careProfileId = "", caregiverUserId = "") => {
  const ids = scheduleStaffIds(schedule);
  return ids.includes(String(careProfileId || "")) || Boolean(caregiverUserId && ids.includes(String(caregiverUserId)));
};
const defaultForm = (location = "", date = today()) => ({
  careProfileId: "",
  startTimestamp: `${date}T09:00`,
  endTimestamp: `${date}T17:00`,
  scheduleType: "SCHEDULED",
  status: "ACTIVE",
  title: "",
  location,
  notes: "",
});
const formFromSchedule = (schedule = {}) => ({
  careProfileId: schedule.careProfileId || schedule.caregiverProfileId || schedule.careGiverProfileId || "",
  startTimestamp: dateTimeInputValue(schedule.startTimestamp),
  endTimestamp: dateTimeInputValue(schedule.endTimestamp, String(schedule.startTimestamp || "").slice(0, 10) || today(), "17:00"),
  scheduleType: schedule.scheduleType || "SCHEDULED",
  status: schedule.status || "ACTIVE",
  title: schedule.title || "",
  location: schedule.location || "",
  notes: schedule.notes || "",
});

export default function CareStaffSchedulesPage({ scope = "workspace" }) {
  const { user, careRoles = [], refreshCareRoles } = useAuth();
  const { selectedWorkspace, selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const actorId = currentUserId(user);
  const normalizedCareRoles = careRoles.map(normalizeRoleToken);
  const canManageSchedules = normalizedCareRoles.includes("care_owner") || normalizedCareRoles.includes("care_admin");
  const isMine = scope === "mine";
  const [profile, setProfile] = useState(null);
  const [careProfiles, setCareProfiles] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [rangeStart, setRangeStart] = useState(() => today());
  const [rangeEnd, setRangeEnd] = useState(() => daysFromNow(6));
  const [viewMode, setViewMode] = useState("calendar");
  const [calendarMonth, setCalendarMonth] = useState(() => parseDateKey(today()));
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(() => today());
  const [staffFilter, setStaffFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [calendarFullscreenOpen, setCalendarFullscreenOpen] = useState(false);
  const [calendarMoreDay, setCalendarMoreDay] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(() => defaultForm());
  const [businessLocation, setBusinessLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cancellingSchedule, setCancellingSchedule] = useState(false);
  const [deletingSchedule, setDeletingSchedule] = useState(false);
  const [printedAtLabel, setPrintedAtLabel] = useState("");
  const [error, setError] = useState("");
  const [formWarning, setFormWarning] = useState("");

  const workspaceParams = useMemo(
    () => ({ wid: selectedWorkspaceId, workspaceId: selectedWorkspaceId }),
    [selectedWorkspaceId],
  );
  const organizationId = selectedWorkspace?.organizationId || selectedWorkspace?.businessEntityId || "";
  const calendarFetchRange = useMemo(() => calendarRangeForMonth(calendarMonth), [calendarMonth]);
  const activeFetchRange = useMemo(() => (
    viewMode === "calendar"
      ? calendarFetchRange
      : { startDate: rangeStart, endDate: rangeEnd }
  ), [calendarFetchRange, rangeEnd, rangeStart, viewMode]);

  useEffect(() => {
    if (!actorId || !refreshCareRoles || !selectedWorkspaceId) return;
    refreshCareRoles({ workspaceId: selectedWorkspaceId, wid: selectedWorkspaceId });
  }, [actorId, refreshCareRoles, selectedWorkspaceId]);

  const loadData = async (overrides = {}) => {
    if (!workspaceListReady || !selectedWorkspaceId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const params = {
      ...workspaceParams,
      startDate: overrides.rangeStart || activeFetchRange.startDate,
      endDate: overrides.rangeEnd || activeFetchRange.endDate,
      ...(typeFilter !== "ALL" ? { scheduleType: typeFilter } : {}),
      ...(statusFilter !== "ALL" && backendScheduleStatuses.has(statusFilter) ? { status: statusFilter } : {}),
      ...(!isMine && staffFilter !== "ALL" ? { careProfileId: staffFilter } : {}),
    };
    if (isMine) {
      try {
        const result = await careService.careProfiles.getMine({ actorId, userId: actorId, ...workspaceParams });
        const resolvedProfile = normalizeCareProfile(result);
        setProfile(resolvedProfile);
        setCareProfiles(resolvedProfile ? [resolvedProfile] : []);
        const profileId = profileIdFor(resolvedProfile || {});
        if (!profileId) {
          setSchedules([]);
          setError("No completed care profile found for your account.");
          return;
        }
        const rows = await careService.schedules.listForCareProfile(profileId, params);
        setSchedules(Array.isArray(rows) ? rows : []);
      } catch (loadError) {
        setSchedules([]);
        setError(loadError?.response?.data?.message || loadError?.message || "Unable to load schedule.");
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const [scheduleResult, profilesResult] = await Promise.allSettled([
        careService.schedules.list(params),
        careService.careProfiles.list(workspaceParams),
      ]);
      setSchedules(scheduleResult.status === "fulfilled" && Array.isArray(scheduleResult.value) ? scheduleResult.value : []);
      setCareProfiles(profilesResult.status === "fulfilled" && Array.isArray(profilesResult.value) ? profilesResult.value : []);
      if (scheduleResult.status === "rejected") {
        setError(scheduleResult.reason?.response?.data?.message || scheduleResult.reason?.message || "Unable to load schedules.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorId, calendarMonth, isMine, rangeEnd, rangeStart, selectedWorkspaceId, staffFilter, statusFilter, typeFilter, viewMode, workspaceListReady]);

  useEffect(() => {
    const effectiveOrganizationId = selectedWorkspace?.organizationId || selectedWorkspace?.businessEntityId || "";
    if (!effectiveOrganizationId) {
      setBusinessLocation("");
      return undefined;
    }
    let active = true;
    Promise.allSettled([
      olreadiaService.organizations.get(effectiveOrganizationId),
      olreadiaService.organizations.locations.list(effectiveOrganizationId, { page: 0, size: 20 }),
    ]).then(([organizationResult, locationsResult]) => {
      if (!active) return;
      const organization = organizationResult.status === "fulfilled" ? organizationResult.value : {};
      const page = locationsResult.status === "fulfilled" ? locationsResult.value : [];
      const locations = Array.isArray(page?.content) ? page.content : Array.isArray(page) ? page : [];
      const firstLocation = locations[0] || organizationLocationsOf(organization)[0] || organization.locationInformation || organization.location || organization.address;
      setBusinessLocation(addressFromLocationInfo(firstLocation));
    }).catch(() => {
      if (active) setBusinessLocation("");
    });
    return () => {
      active = false;
    };
  }, [selectedWorkspace?.businessEntityId, selectedWorkspace?.organizationId]);

  useEffect(() => {
    if (!editorOpen || editingSchedule || !businessLocation) return;
    setForm((current) => current.location ? current : { ...current, location: businessLocation });
  }, [businessLocation, editingSchedule, editorOpen]);

  const profilesById = useMemo(() => {
    const lookup = {};
    careProfiles.forEach((careProfile) => {
      const profileId = profileIdFor(careProfile);
      const userId = userIdFor(careProfile);
      if (profileId) lookup[profileId] = careProfile;
      if (userId) lookup[userId] = careProfile;
    });
    return lookup;
  }, [careProfiles]);
  const staffChoices = useMemo(
    () => careProfiles
      .map((careProfile) => ({
        id: profileIdFor(careProfile),
        userId: userIdFor(careProfile),
        name: nameFor(careProfile),
        email: emailFor(careProfile),
      }))
      .filter((item) => item.id)
      .sort((left, right) => left.name.localeCompare(right.name)),
    [careProfiles],
  );
  const filteredSchedules = useMemo(() => schedules.filter((schedule) => {
    if (staffFilter !== "ALL") {
      const selectedStaff = staffChoices.find((staff) => staff.id === staffFilter);
      const ids = [schedule.careProfileId, schedule.caregiverUserId].map((value) => String(value || ""));
      if (!ids.includes(staffFilter) && (!selectedStaff?.userId || !ids.includes(String(selectedStaff.userId)))) return false;
    }
    if (statusFilter !== "ALL" && scheduleStatusValue(schedule) !== statusFilter) {
      return false;
    }
    return true;
  }), [schedules, staffChoices, staffFilter, statusFilter]);
  const statusFilterOptions = useMemo(() => {
    const values = new Set(["ALL", ...scheduleStatusOptions]);
    schedules.forEach((schedule) => {
      const status = scheduleStatusValue(schedule);
      if (status) values.add(status);
    });
    if (statusFilter && statusFilter !== "ALL") values.add(statusFilter);
    return Array.from(values);
  }, [schedules, statusFilter]);
  const totalCount = filteredSchedules.length;
  const activeCount = filteredSchedules.filter((schedule) => scheduleStatusValue(schedule) === "ACTIVE").length;
  const completedCount = filteredSchedules.filter((schedule) => scheduleStatusValue(schedule) === "COMPLETED").length;
  const cancelledCount = filteredSchedules.filter((schedule) => ["CANCELLED", "CANCELED"].includes(scheduleStatusValue(schedule))).length;
  const scheduledCount = filteredSchedules.filter((schedule) => schedule.scheduleType === "SCHEDULED" && schedule.status !== "CANCELLED").length;
  const onCallCount = filteredSchedules.filter((schedule) => schedule.scheduleType === "ON_CALL" && schedule.status !== "CANCELLED").length;
  const calendarDays = useMemo(() => calendarDaysFor(calendarMonth), [calendarMonth]);
  const selectedDateSchedules = useMemo(
    () => filteredSchedules.filter((schedule) => overlapsDay(schedule, parseDateKey(selectedCalendarDate))),
    [filteredSchedules, selectedCalendarDate],
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const clearPrintMode = () => document.body.classList.remove("printing-schedules");
    window.addEventListener("afterprint", clearPrintMode);
    return () => {
      window.removeEventListener("afterprint", clearPrintMode);
      clearPrintMode();
    };
  }, []);

  const exportScheduleRecordsToExcel = () => {
    if (typeof window === "undefined") return;

    const rows = filteredSchedules.length ? filteredSchedules.map((schedule) => [
      staffNameFor(schedule, profilesById),
      staffEmailFor(schedule, profilesById),
      labelFor(schedule.scheduleType),
      labelFor(scheduleStatusValue(schedule) || "ACTIVE"),
      formatTimestamp(scheduleStartValue(schedule)),
      formatTimestamp(scheduleEndValue(schedule)),
      scheduleDurationLabel(schedule),
      schedule.location || "",
      schedule.notes || "",
    ]) : [["No schedule records found for this range.", "", "", "", "", "", "", "", ""]];
    const html = `<!doctype html>
<html>
<head><meta charset="utf-8" /></head>
<body>
  <table>
    <thead>
      <tr><th>Staff</th><th>Email</th><th>Type</th><th>Status</th><th>Start</th><th>End</th><th>Duration</th><th>Location</th><th>Notes</th></tr>
    </thead>
    <tbody>
      ${rows.map((cells) => `<tr>${cells.map((cell) => `<td>${excelCell(cell)}</td>`).join("")}</tr>`).join("")}
    </tbody>
  </table>
</body>
</html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `schedules-${filenameDate()}.xls`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const printScheduleRecords = () => {
    if (typeof window === "undefined") return;
    setPrintedAtLabel(new Date().toLocaleString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }));
    document.body.classList.add("printing-schedules");
    window.setTimeout(() => window.print(), 50);
  };

  const openNew = (date = today()) => {
    if (!canManageSchedules) {
      setError("Only care owners and admins can create schedules.");
      return;
    }
    setCalendarFullscreenOpen(false);
    setEditingSchedule(null);
    setFormWarning("");
    setForm({
      ...defaultForm(businessLocation, date),
      careProfileId: !isMine && staffFilter !== "ALL" ? staffFilter : "",
    });
    setEditorOpen(true);
  };
  const pickCalendarDate = (date, shouldOpenEditor = false) => {
    const dateKey = localDateKey(date);
    setSelectedCalendarDate(dateKey);
    if (!isMine && shouldOpenEditor) {
      openNew(dateKey);
    }
  };
  const openScheduleDetails = (schedule) => {
    setCalendarFullscreenOpen(false);
    setCalendarMoreDay(null);
    setSelectedSchedule(schedule);
  };
  const openCalendarMoreDay = (date, daySchedules = []) => {
    const dateKey = localDateKey(date);
    setSelectedCalendarDate(dateKey);
    setCalendarMoreDay({
      dateKey,
      label: date.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" }),
      schedules: [...daySchedules].sort((left, right) => new Date(scheduleStartValue(left)) - new Date(scheduleStartValue(right))),
    });
  };
  const openEdit = (schedule) => {
    if (!canManageSchedules) {
      setError("Only care owners and admins can edit schedules.");
      return;
    }
    setEditingSchedule(schedule);
    setSelectedSchedule(null);
    setFormWarning("");
    setForm(formFromSchedule(schedule));
    setEditorOpen(true);
  };
  const closeEditor = () => {
    setEditorOpen(false);
    setEditingSchedule(null);
    setFormWarning("");
    setForm(defaultForm(businessLocation, selectedCalendarDate));
  };
  const cancelSchedule = async (schedule) => {
    if (!canManageSchedules) {
      setError("Only care owners and admins can cancel schedules.");
      return;
    }
    const scheduleId = scheduleIdFor(schedule);
    const careProfileId = schedule?.careProfileId || schedule?.caregiverProfileId || schedule?.careGiverProfileId || "";
    const startTimestamp = scheduleStartValue(schedule);
    const endTimestamp = scheduleEndValue(schedule);
    if (!scheduleId || !careProfileId || !startTimestamp || !endTimestamp) {
      const validationMessage = "Unable to cancel schedule because required schedule details are missing.";
      setError(validationMessage);
      setFormWarning(validationMessage);
      message.warning(validationMessage);
      return;
    }
    setCancellingSchedule(true);
    setError("");
    setFormWarning("");
    try {
      await careService.schedules.update(scheduleId, {
        actorId,
        careProfileId,
        workspaceId: selectedWorkspaceId,
        organizationId: schedule.organizationId || organizationId,
        startTimestamp,
        endTimestamp,
        scheduleType: schedule.scheduleType || "SCHEDULED",
        status: "CANCELLED",
        title: schedule.title || "",
        location: schedule.location || "",
        notes: schedule.notes || "",
      }, workspaceParams);
      message.success("Schedule cancelled.");
      setSelectedSchedule(null);
      if (editingSchedule && scheduleIdFor(editingSchedule) === scheduleId) {
        closeEditor();
      }
      await loadData();
    } catch (cancelError) {
      const cancelMessage = cancelError?.response?.data?.message || cancelError?.message || "Unable to cancel schedule.";
      setError(cancelMessage);
      setFormWarning(cancelMessage);
      message.error(cancelMessage);
    } finally {
      setCancellingSchedule(false);
    }
  };
  const confirmCancelSchedule = (schedule) => {
    const staffLabel = staffNameFor(schedule, profilesById);
    Modal.confirm({
      title: "Cancel schedule?",
      content: `Cancel this schedule for ${staffLabel}? It will remain visible with cancelled status.`,
      okText: "Cancel schedule",
      cancelText: "Keep schedule",
      centered: true,
      onOk: () => cancelSchedule(schedule),
    });
  };
  const deleteSchedule = async (schedule) => {
    if (!canManageSchedules) {
      setError("Only care owners and admins can delete schedules.");
      return;
    }
    const scheduleId = scheduleIdFor(schedule);
    if (!scheduleId) {
      const validationMessage = "Unable to delete schedule because the schedule id is missing.";
      setError(validationMessage);
      setFormWarning(validationMessage);
      message.warning(validationMessage);
      return;
    }
    setDeletingSchedule(true);
    setError("");
    setFormWarning("");
    try {
      await careService.schedules.delete(scheduleId, { actorId, ...workspaceParams });
      message.success("Schedule deleted.");
      setSelectedSchedule(null);
      if (editingSchedule && scheduleIdFor(editingSchedule) === scheduleId) {
        closeEditor();
      }
      await loadData();
    } catch (deleteError) {
      const deleteMessage = deleteError?.response?.data?.message || deleteError?.message || "Unable to delete schedule.";
      setError(deleteMessage);
      setFormWarning(deleteMessage);
      message.error(deleteMessage);
    } finally {
      setDeletingSchedule(false);
    }
  };
  const confirmDeleteSchedule = (schedule) => {
    const staffLabel = staffNameFor(schedule, profilesById);
    Modal.confirm({
      title: "Delete schedule?",
      content: `Delete this schedule for ${staffLabel}? This action cannot be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      centered: true,
      onOk: () => deleteSchedule(schedule),
    });
  };
  const saveSchedule = async () => {
    if (!canManageSchedules) {
      setError("Only care owners and admins can save schedules.");
      return;
    }
    const careProfileId = isMine ? profileIdFor(profile || {}) : form.careProfileId;
    if (!careProfileId) {
      const validationMessage = "Select a care staff member.";
      setError(validationMessage);
      setFormWarning(validationMessage);
      message.warning(validationMessage);
      return;
    }
    const selectedStaff = staffChoices.find((staff) => staff.id === careProfileId);
    const startTimestamp = zonedIsoFromDateTimeInput(form.startTimestamp);
    const endTimestamp = zonedIsoFromDateTimeInput(form.endTimestamp);
    const nextStart = new Date(startTimestamp || "");
    const nextEnd = new Date(endTimestamp || "");
    if (Number.isNaN(nextStart.getTime()) || Number.isNaN(nextEnd.getTime()) || nextEnd <= nextStart) {
      const validationMessage = "End time must be after start time.";
      setError(validationMessage);
      setFormWarning(validationMessage);
      message.warning(validationMessage);
      return;
    }
    setSaving(true);
    setError("");
    setFormWarning("");
    try {
      const shouldCheckOverlap = statusIsActive(form.status);
      const conflictingSchedule = shouldCheckOverlap
        ? schedules.find((schedule) => {
          if (!schedule || !scheduleIsActive(schedule)) return false;
          if (scheduleIdFor(schedule) && scheduleIdFor(schedule) === scheduleIdFor(editingSchedule)) return false;
          if (!sameCareStaffSchedule(schedule, careProfileId, selectedStaff?.userId)) return false;
          const existingStart = new Date(scheduleStartValue(schedule));
          const existingEnd = new Date(scheduleEndValue(schedule));
          if (Number.isNaN(existingStart.getTime()) || Number.isNaN(existingEnd.getTime())) return false;
          return rangesOverlap(nextStart, nextEnd, existingStart, existingEnd);
        })
        : null;
      if (conflictingSchedule) {
        const conflictLabel = `${formatTimestamp(conflictingSchedule.startTimestamp)} - ${formatTimestamp(conflictingSchedule.endTimestamp)}`;
        const overlapMessage = `${staffNameFor(conflictingSchedule, profilesById)} already has an active schedule that overlaps this time: ${conflictLabel}.`;
        setError(overlapMessage);
        setFormWarning(overlapMessage);
        message.warning(overlapMessage);
        return;
      }
      const payload = {
        actorId,
        careProfileId,
        workspaceId: selectedWorkspaceId,
        organizationId,
        startTimestamp,
        endTimestamp,
        scheduleType: form.scheduleType,
        status: form.status,
        title: form.title,
        location: form.location,
        notes: form.notes,
      };
      const saved = editingSchedule?.id
        ? await careService.schedules.update(editingSchedule.id, payload, workspaceParams)
        : await careService.schedules.create(payload, workspaceParams);
      const successMessage = editingSchedule?.id ? "Schedule updated." : "Schedule added.";
      closeEditor();
      message.success(successMessage);
      const savedStartKey = dateKeyFromTimestamp(saved?.startTimestamp || startTimestamp);
      setSelectedCalendarDate(savedStartKey);
      if (viewMode === "calendar") {
        const savedMonth = parseDateKey(savedStartKey);
        setCalendarMonth(savedMonth);
        const savedMonthRange = calendarRangeForMonth(savedMonth);
        await loadData({ rangeStart: savedMonthRange.startDate, rangeEnd: savedMonthRange.endDate });
      } else {
        await loadData();
      }
    } catch (saveError) {
      setError(saveError?.response?.data?.message || saveError?.message || "Unable to save schedule.");
    } finally {
      setSaving(false);
    }
  };
  const updateFormStaff = (careProfileId) => {
    const staff = staffChoices.find((item) => item.id === careProfileId);
    setFormWarning("");
    setForm((current) => ({
      ...current,
      careProfileId: careProfileId || "",
      title: scheduleTitleFor(staff, current.scheduleType) || current.title,
      location: current.location || businessLocation,
    }));
  };
  const updateFormScheduleType = (scheduleType) => {
    setFormWarning("");
    setForm((current) => {
      const staff = staffChoices.find((item) => item.id === current.careProfileId);
      return {
        ...current,
        scheduleType,
        title: scheduleTitleFor(staff, scheduleType) || current.title,
      };
    });
  };
  const renderPrintArea = () => (
    <section className="schedule-print-area" aria-hidden="true">
      <header>
        <div>
          <h1>{isMine ? "My Schedule" : "Care Staff Schedule"}</h1>
          <p>{isMine ? "Review your normal schedule and on-call coverage." : "Plan shifts, see coverage, and keep schedule conflicts visible."}</p>
        </div>
        <aside>
          <span>Printed</span>
          <strong>{printedAtLabel}</strong>
        </aside>
      </header>
      <div className="timesheet-print-meta">
        <span>Range: <strong>{viewMode === "calendar" ? `${activeFetchRange.startDate || "Any"} - ${activeFetchRange.endDate || "Any"}` : `${rangeStart || "Any"} - ${rangeEnd || "Any"}`}</strong></span>
        <span>Staff: <strong>{staffFilter === "ALL" ? "All staff" : staffChoices.find((staff) => staff.id === staffFilter)?.name || "Selected staff"}</strong></span>
        <span>Type: <strong>{typeFilter === "ALL" ? "All types" : labelFor(typeFilter)}</strong></span>
        <span>Status: <strong>{statusFilter === "ALL" ? "All statuses" : labelFor(statusFilter)}</strong></span>
        <span>Total: <strong>{filteredSchedules.length}</strong></span>
      </div>
      <table>
        <thead>
          <tr>
            <th>Staff</th>
            <th>Type</th>
            <th>Status</th>
            <th>Start</th>
            <th>End</th>
            <th>Duration</th>
            <th>Location</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {filteredSchedules.length ? filteredSchedules.map((schedule) => (
            <tr key={`print-${scheduleIdFor(schedule) || scheduleStartValue(schedule)}`}>
              <td>
                <strong>{staffNameFor(schedule, profilesById)}</strong>
                <small>{staffEmailFor(schedule, profilesById)}</small>
              </td>
              <td>{labelFor(schedule.scheduleType)}</td>
              <td>{labelFor(scheduleStatusValue(schedule) || "ACTIVE")}</td>
              <td>{formatTimestamp(scheduleStartValue(schedule))}</td>
              <td>{formatTimestamp(scheduleEndValue(schedule))}</td>
              <td>{scheduleDurationLabel(schedule)}</td>
              <td>{schedule.location || ""}</td>
              <td>{schedule.notes || ""}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan={8}>No schedule records found for this range.</td>
            </tr>
          )}
        </tbody>
      </table>
    </section>
  );
  const renderCalendarGrid = (mode = "inline") => {
    const isFullscreen = mode === "fullscreen";
    const visibleScheduleLimit = 2;
    return (
      <>
        <div className={`schedule-calendar${isFullscreen ? " is-fullscreen" : ""}`}>
          {weekdayLabels.map((day) => <div className="schedule-calendar-weekday" key={day}>{day}</div>)}
          {calendarDays.map((date) => {
            const dateKey = localDateKey(date);
            const daySchedules = filteredSchedules
              .filter((schedule) => overlapsDay(schedule, date))
              .sort((left, right) => new Date(scheduleStartValue(left)) - new Date(scheduleStartValue(right)));
            const isCurrentMonth = date.getMonth() === calendarMonth.getMonth();
            const isSelected = selectedCalendarDate === dateKey;
            return (
              <article className={`schedule-calendar-day${isCurrentMonth ? "" : " is-muted"}${isSelected ? " is-selected" : ""}${sameDay(date, new Date()) ? " is-today" : ""}`} key={dateKey}>
                <button className="schedule-calendar-date" type="button" onClick={() => pickCalendarDate(date)}>
                  <span>{date.getDate()}</span>
                  {daySchedules.length ? <small>{daySchedules.length}</small> : null}
                </button>
                <div className="schedule-calendar-events">
                  {daySchedules.slice(0, visibleScheduleLimit).map((schedule) => (
                    <button className={`schedule-calendar-event is-${String(schedule.scheduleType || "").toLowerCase()} status-${statusClassFor(schedule.status)}`} key={schedule.id} type="button" onClick={() => openScheduleDetails(schedule)}>
                      <span className="schedule-calendar-event-time">
                        <i className={`ti ${statusIconFor(schedule.status)}`} title={labelFor(schedule.status)} />
                        {timeRangeLabel(schedule)}
                      </span>
                      <strong>{staffNameFor(schedule, profilesById)}</strong>
                    </button>
                  ))}
                  {daySchedules.length > visibleScheduleLimit ? (
                    <button className="schedule-calendar-more" type="button" onClick={() => openCalendarMoreDay(date, daySchedules)}>
                      +{daySchedules.length - visibleScheduleLimit} more
                    </button>
                  ) : null}
                </div>
                {!isMine ? (
                  <Guard roles={["care_owner", "care_admin"]}>
                    <button className="schedule-calendar-add" type="button" onClick={() => pickCalendarDate(date, true)} title="Add schedule on this date">
                      <i className="ti ti-plus" />
                    </button>
                  </Guard>
                ) : null}
              </article>
            );
          })}
        </div>
        <div className="schedule-calendar-selected">
          <div>
            <span>Selected date</span>
            <strong>{parseDateKey(selectedCalendarDate).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" })}</strong>
          </div>
          <div>
            <span>Schedules</span>
            <strong>{selectedDateSchedules.length}</strong>
          </div>
          {!isMine ? (
            <Guard roles={["care_owner", "care_admin"]}>
              <button className="mini-btn timesheet-nav-blue" type="button" onClick={() => openNew(selectedCalendarDate)}>
                <i className="ti ti-calendar-plus" /> Add on selected date
              </button>
            </Guard>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <CareShell className="dashboard-content residents-page care-staff-schedules-page">
      <section className="schedule-command-center">
        <div className="schedule-command-head">
          <div className="page-title">
            <span className="schedule-page-kicker"><i className="ti ti-calendar-clock" /> Staff Operations</span>
            <h1>{isMine ? "My Schedule" : "Care Staff Schedule"}</h1>
            <p>{isMine ? "Review your normal schedule and on-call coverage." : "Plan shifts, see coverage, and keep schedule conflicts visible."}</p>
          </div>
          <div className="care-staff-page-actions">
            {!isMine ? (
              <Guard roles={["care_owner", "care_admin"]}>
                <button className="btn primary schedule-primary-action" type="button" onClick={() => openNew(selectedCalendarDate)}>
                  <i className="ti ti-calendar-plus" /> Add schedule
                </button>
              </Guard>
            ) : null}
            <button className="mini-btn schedule-refresh-action" type="button" onClick={loadData}>
              <i className="ti ti-refresh" /> Refresh
            </button>
            <button className="mini-btn timesheet-export-excel-button" type="button" onClick={exportScheduleRecordsToExcel}>
              <i className="ti ti-file-spreadsheet" /> Export Excel
            </button>
            <button className="mini-btn" type="button" onClick={printScheduleRecords}>
              <i className="ti ti-printer" /> Print records
            </button>
          </div>
        </div>

        <div className="schedule-status-strip">
          <article className="schedule-status-card is-total">
            <span><i className="ti ti-calendar-stats" /> Total</span>
            <strong>{totalCount}</strong>
            <small>{scheduledCount} normal · {onCallCount} on call</small>
          </article>
          <article className="schedule-status-card is-active">
            <span><i className="ti ti-clock" /> Active</span>
            <strong>{activeCount}</strong>
            <small>Blocking overlaps</small>
          </article>
          <article className="schedule-status-card is-completed">
            <span><i className="ti ti-circle-check" /> Completed</span>
            <strong>{completedCount}</strong>
            <small>Finished coverage</small>
          </article>
          <article className="schedule-status-card is-cancelled">
            <span><i className="ti ti-ban" /> Cancelled</span>
            <strong>{cancelledCount}</strong>
            <small>Kept for history</small>
          </article>
        </div>
      </section>

      {loading ? <Panel title="Schedule"><p>Loading schedule...</p></Panel> : null}
      {error ? <Panel title="Schedule"><p className="form-error">{error}</p></Panel> : null}

      <section className={`timesheet-filter-bar schedule-control-panel${isMine ? " is-personal" : ""}`}>
        {!isMine ? (
          <label>
            Staff
            <Select
              showSearch
              value={staffFilter}
              optionFilterProp="searchText"
              onChange={(value) => setStaffFilter(value || "ALL")}
              options={[
                { value: "ALL", searchText: "All staff", label: "All staff" },
                ...staffChoices.map((staff) => ({
                  value: staff.id,
                  searchText: `${staff.name} ${staff.email}`,
                  label: (
                    <span className="timesheet-staff-option">
                      <strong>{staff.name}</strong>
                      <small>{staff.email || "No email"}</small>
                    </span>
                  ),
                })),
              ]}
            />
          </label>
        ) : null}
        <label>
          Type
          <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
            <option value="ALL">Normal and on call</option>
            <option value="SCHEDULED">Normal schedule</option>
            <option value="ON_CALL">On call</option>
          </select>
        </label>
        <label>
          Status
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {statusFilterOptions.map((status) => (
              <option value={status} key={status}>{status === "ALL" ? "All statuses" : labelFor(status)}</option>
            ))}
          </select>
        </label>
        <div className="schedule-view-toggle" role="group" aria-label="Schedule view">
          <button className={viewMode === "calendar" ? "is-active" : ""} type="button" onClick={() => setViewMode("calendar")}>
            <i className="ti ti-calendar-month" /> Calendar
          </button>
          <button className={viewMode === "board" ? "is-active" : ""} type="button" onClick={() => setViewMode("board")}>
            <i className="ti ti-layout-grid" /> Board
          </button>
        </div>
      </section>

      {renderPrintArea()}

      {viewMode === "calendar" ? (
        <Panel title={isMine ? "My Calendar" : "Schedule Calendar"}>
          <div className="schedule-calendar-toolbar">
            <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, -1))}>
              <i className="ti ti-chevron-left" /> Previous
            </button>
            <strong>{monthLabel(calendarMonth)}</strong>
            <div className="schedule-calendar-toolbar-actions">
              <button className="mini-btn" type="button" onClick={() => setCalendarFullscreenOpen(true)}>
                <i className="ti ti-maximize" /> Full screen
              </button>
              <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, 1))}>
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
          {renderCalendarGrid()}
        </Panel>
      ) : (
        <>
          <section className="schedule-board-range">
            <label>
              Range start
              <input type="date" value={rangeStart} onChange={(event) => setRangeStart(event.target.value)} />
            </label>
            <label>
              Range end
              <input type="date" value={rangeEnd} onChange={(event) => setRangeEnd(event.target.value)} />
            </label>
          </section>
          <Panel title={isMine ? "My Schedule" : "Schedule Board"}>
            <div className="schedule-board">
              {filteredSchedules.length ? filteredSchedules.map((schedule) => (
                <article className={`schedule-card is-${String(schedule.scheduleType || "").toLowerCase()}`} key={schedule.id} role="button" tabIndex={0} onClick={() => openScheduleDetails(schedule)} onKeyDown={(event) => event.key === "Enter" && openScheduleDetails(schedule)}>
                  <div className="schedule-card-type">
                    <i className={`ti ${schedule.scheduleType === "ON_CALL" ? "ti-phone-call" : "ti-calendar-clock"}`} />
                    <span>{labelFor(schedule.scheduleType)}</span>
                  </div>
                  <strong>{schedule.title || staffNameFor(schedule, profilesById)}</strong>
                  {!isMine ? <small>{staffEmailFor(schedule, profilesById)}</small> : null}
                  <p>{formatTimestamp(schedule.startTimestamp)} - {formatTimestamp(schedule.endTimestamp)}</p>
                  {schedule.location ? <em>{schedule.location}</em> : null}
                  <div className="schedule-card-foot">
                    <span className={`schedule-status is-${statusClassFor(schedule.status)}`}>
                      <i className={`ti ${statusIconFor(schedule.status)}`} />
                      {labelFor(schedule.status)}
                    </span>
                    {!isMine ? (
                      <Guard roles={["care_owner", "care_admin"]}>
                        <button className="mini-btn" type="button" onClick={(event) => { event.stopPropagation(); openEdit(schedule); }}>
                          <i className="ti ti-edit" /> Edit
                        </button>
                      </Guard>
                    ) : null}
                  </div>
                </article>
              )) : (
                <div className="timesheet-empty">
                  <i className="ti ti-calendar-off" />
                  <span>No schedule records found for this range.</span>
                </div>
              )}
            </div>
          </Panel>
        </>
      )}

      <Modal
        className="schedule-calendar-fullscreen-modal"
        title={isMine ? "My Schedule Calendar" : "Care Staff Schedule Calendar"}
        open={calendarFullscreenOpen}
        footer={null}
        width="calc(100vw - 48px)"
        centered
        onCancel={() => setCalendarFullscreenOpen(false)}
      >
        <div className="schedule-calendar-fullscreen-shell">
          <div className="schedule-calendar-toolbar">
            <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, -1))}>
              <i className="ti ti-chevron-left" /> Previous
            </button>
            <strong>{monthLabel(calendarMonth)}</strong>
            <button className="mini-btn" type="button" onClick={() => setCalendarMonth((current) => addMonths(current, 1))}>
              Next <i className="ti ti-chevron-right" />
            </button>
          </div>
          {renderCalendarGrid("fullscreen")}
        </div>
      </Modal>

      <Modal
        className="schedule-calendar-more-modal"
        title={calendarMoreDay?.label || "Schedules"}
        open={Boolean(calendarMoreDay)}
        footer={null}
        width={520}
        centered
        onCancel={() => setCalendarMoreDay(null)}
      >
        <div className="schedule-calendar-more-list">
          {(calendarMoreDay?.schedules || []).map((schedule) => (
            <button className={`schedule-calendar-more-row status-${statusClassFor(schedule.status)}`} key={schedule.id || `${scheduleStartValue(schedule)}-${staffNameFor(schedule, profilesById)}`} type="button" onClick={() => openScheduleDetails(schedule)}>
              <span className="schedule-calendar-more-row__time">
                <i className={`ti ${statusIconFor(schedule.status)}`} />
                {timeRangeLabel(schedule)}
              </span>
              <strong>{staffNameFor(schedule, profilesById)}</strong>
              <small>{labelFor(schedule.scheduleType)} · {labelFor(schedule.status || "ACTIVE")}</small>
            </button>
          ))}
        </div>
      </Modal>

      <Drawer width={520} title={editingSchedule?.id ? "Edit Schedule" : "Add Schedule"} open={editorOpen} onClose={closeEditor} destroyOnClose>
        <div className="schedule-editor">
          {formWarning ? (
            <div className="schedule-form-warning" role="alert">
              <i className="ti ti-alert-triangle" />
              <span>{formWarning}</span>
            </div>
          ) : null}
          <label>
            Staff
            <Select
              showSearch
              value={form.careProfileId || undefined}
              optionFilterProp="searchText"
              onChange={updateFormStaff}
              placeholder="Search staff by name or email"
              options={staffChoices.map((staff) => ({
                value: staff.id,
                searchText: `${staff.name} ${staff.email}`,
                label: (
                  <span className="timesheet-staff-option">
                    <strong>{staff.name}</strong>
                    <small>{staff.email || "No email"}</small>
                  </span>
                ),
              }))}
            />
          </label>
          <div className="schedule-editor-grid">
            <label>
              Start
              <input type="datetime-local" value={form.startTimestamp} onChange={(event) => { setFormWarning(""); setForm((current) => ({ ...current, startTimestamp: event.target.value })); }} />
            </label>
            <label>
              End
              <input type="datetime-local" value={form.endTimestamp} onChange={(event) => { setFormWarning(""); setForm((current) => ({ ...current, endTimestamp: event.target.value })); }} />
            </label>
          </div>
          <div className="schedule-editor-grid">
            <label>
              Type
              <select value={form.scheduleType} onChange={(event) => updateFormScheduleType(event.target.value)}>
                <option value="SCHEDULED">Normal schedule</option>
                <option value="ON_CALL">On call</option>
              </select>
            </label>
            <label>
              Status
              <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}>
                <option value="ACTIVE">Active</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="COMPLETED">Completed</option>
              </select>
            </label>
          </div>
          <label>
            Title
            <input value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} placeholder="e.g. Evening shift, weekend on call" />
          </label>
          <label>
            Location
            <input value={form.location} onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))} placeholder="Facility, unit, or service area" />
          </label>
          <label>
            Notes
            <textarea value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} placeholder="Schedule notes" />
          </label>
          <div className="drawer-actions">
            <button className="mini-btn" type="button" onClick={closeEditor}>Close</button>
            {editingSchedule ? (
              <>
                {scheduleStatusValue(editingSchedule) !== "CANCELLED" ? (
                  <button className="mini-btn warning" type="button" onClick={() => confirmCancelSchedule(editingSchedule)} disabled={cancellingSchedule || deletingSchedule || saving}>
                    <i className="ti ti-ban" /> {cancellingSchedule ? "Cancelling..." : "Cancel schedule"}
                  </button>
                ) : null}
                <button className="mini-btn danger" type="button" onClick={() => confirmDeleteSchedule(editingSchedule)} disabled={deletingSchedule || cancellingSchedule || saving}>
                  <i className="ti ti-trash" /> {deletingSchedule ? "Deleting..." : "Delete"}
                </button>
              </>
            ) : null}
            <button className="btn primary" type="button" onClick={saveSchedule} disabled={saving}>
              <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save schedule"}
            </button>
          </div>
        </div>
      </Drawer>

      <Drawer width={520} title="Schedule Details" open={Boolean(selectedSchedule)} onClose={() => setSelectedSchedule(null)} destroyOnClose>
        {selectedSchedule ? (
          <div className="schedule-details-drawer">
            <section>
              <span>Care staff</span>
              <strong>{staffNameFor(selectedSchedule, profilesById)}</strong>
              <small>{staffEmailFor(selectedSchedule, profilesById)}</small>
            </section>
            <section className="schedule-editor-grid">
              <div>
                <span>Type</span>
                <strong>{labelFor(selectedSchedule.scheduleType)}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{labelFor(selectedSchedule.status)}</strong>
              </div>
            </section>
            <section>
              <span>Starts</span>
              <strong>{formatTimestamp(selectedSchedule.startTimestamp)}</strong>
            </section>
            <section>
              <span>Ends</span>
              <strong>{formatTimestamp(selectedSchedule.endTimestamp)}</strong>
            </section>
            {selectedSchedule.location ? (
              <section>
                <span>Location</span>
                <p>{selectedSchedule.location}</p>
              </section>
            ) : null}
            {selectedSchedule.notes ? (
              <section>
                <span>Notes</span>
                <p>{selectedSchedule.notes}</p>
              </section>
            ) : null}
            {!isMine ? (
              <Guard roles={["care_owner", "care_admin"]}>
                <div className="drawer-actions">
                  <button className="mini-btn" type="button" onClick={() => openEdit(selectedSchedule)}>
                    <i className="ti ti-edit" /> Edit
                  </button>
                  {scheduleStatusValue(selectedSchedule) !== "CANCELLED" ? (
                    <button className="mini-btn warning" type="button" onClick={() => confirmCancelSchedule(selectedSchedule)} disabled={cancellingSchedule || deletingSchedule}>
                      <i className="ti ti-ban" /> {cancellingSchedule ? "Cancelling..." : "Cancel schedule"}
                    </button>
                  ) : null}
                  <button className="mini-btn danger" type="button" onClick={() => confirmDeleteSchedule(selectedSchedule)} disabled={deletingSchedule || cancellingSchedule}>
                    <i className="ti ti-trash" /> {deletingSchedule ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </Guard>
            ) : null}
          </div>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
